/**
 * WelcomeScreen (Profiles) — device-aware profile picker.
 *
 * Reads the SERVER-SIDE trusted-device record (GET /auth/device/profiles), which
 * survives logout. Two states:
 *  - Untrusted device → "This device isn't set up yet" (no faces; route to sign in).
 *  - Trusted device   → family faces: kids tap to sign in one-tap (no password);
 *    grown-ups are password-gated (protects parental controls).
 * "Forget this device" revokes the trust and reverts to the untrusted state.
 */
import { useState, useEffect } from "react";
import { ChromeTokens } from "../chrome/tokens";
import { Icon } from "../chrome/Icon";
import {
  API_BASE,
  adoptNativeRefreshToken,
  clientHeaders,
  credentialsMode,
  nativeAuthFields,
  setAccessToken,
  setChildSessionToken,
  device,
  type DeviceProfile,
} from "../api";
import { getSilhouetteUrls } from "../avatar/avatarUrlResolver";
import { accountIdFromLogin, useAuth } from "../hooks/useAuth";
import { noteLoginNextUrl } from "../share/useIntent";

interface WelcomeScreenProps {
  onAuthenticated: (session: { type: "child"; childId: string; displayName: string } | { type: "parent" }) => void;
  onNewPerson: () => void;
  onSignup?: () => void;
}

export function WelcomeScreen({ onAuthenticated, onNewPerson, onSignup }: WelcomeScreenProps) {
  const [view, setView] = useState<"loading" | "untrusted" | "trusted">("loading");
  const [family, setFamily] = useState<DeviceProfile[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { refreshSession } = useAuth();

  useEffect(() => {
    let cancelled = false;
    device
      .profiles()
      .then((res) => {
        if (cancelled) return;
        if (res.trusted && res.family.length > 0) {
          setFamily(res.family);
          setView("trusted");
        } else {
          setView("untrusted");
        }
      })
      .catch(() => {
        if (!cancelled) setView("untrusted");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Kid → one tap. The trusted device is the security boundary; no password.
  const kidLogin = async (p: DeviceProfile) => {
    setError("");
    setSubmitting(true);
    try {
      const res = await device.profileLogin(p.id);
      setChildSessionToken(res.child_session_token);
      try { sessionStorage.removeItem("logged_out"); } catch { /* ignore */ }
      onAuthenticated({ type: "child", childId: res.child_id, displayName: res.username });
    } catch {
      setError("That didn't work — ask a grown-up to help.");
    } finally {
      setSubmitting(false);
    }
  };

  // Grown-up → always re-enter a password (even on a trusted device).
  const grownLogin = async (p: DeviceProfile, pw: string) => {
    setError("");
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...clientHeaders() },
        credentials: credentialsMode(),
        body: JSON.stringify({ identifier: p.name, password: pw, ...nativeAuthFields() }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        const detail = (body as { detail?: string }).detail || "";
        setError(detail === "email_not_verified" ? "Check your email for a verification link first." : "That didn't match. Try again.");
        return;
      }
      const data = await resp.json();
      if (data.access_token) {
        setAccessToken(data.access_token);
        localStorage.setItem("access_token", data.access_token);
      }
      adoptNativeRefreshToken(data);
      noteLoginNextUrl(data);
      await refreshSession();
      onAuthenticated(
        data.account_type === "child"
          ? { type: "child", childId: accountIdFromLogin(data), displayName: data.username || p.name }
          : { type: "parent" },
      );
    } catch {
      setError("Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleTap = (p: DeviceProfile) => {
    if (!p.grown) {
      void kidLogin(p);
      return;
    }
    setSelectedId(p.id);
    setPassword("");
    setError("");
  };

  const handleGrownSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const p = family.find((f) => f.id === selectedId);
    if (p && password) void grownLogin(p, password);
  };

  const forget = async () => {
    try {
      await device.forget();
    } catch {
      /* non-fatal */
    }
    setFamily([]);
    setSelectedId(null);
    setPassword("");
    setError("");
    setView("untrusted");
  };

  if (view === "loading") {
    return (
      <div style={pageStyle}>
        <p style={{ fontSize: 14, color: ChromeTokens.color.inkMuted }}>One moment…</p>
      </div>
    );
  }

  // --- Untrusted device: no faces ---
  if (view === "untrusted") {
    return (
      <div style={pageStyle}>
        <div style={{ ...cardStyle, textAlign: "center" }}>
          <div style={lockChipStyle}>
            <Icon name="lock" size={24} stroke={ChromeTokens.color.inkMuted} />
          </div>
          <h2 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 700, color: ChromeTokens.color.ink }}>
            This device isn&apos;t set up yet
          </h2>
          <p style={{ color: ChromeTokens.color.inkMuted, fontSize: 14, margin: 0, lineHeight: 1.5 }}>
            A grown-up signs in once and trusts this device — then everyone in the family can tap their picture to sign in here, no password needed for the kids.
          </p>
          <button onClick={onNewPerson} style={{ ...primaryBtnStyle, marginTop: 16 }}>Sign in</button>
          {onSignup && (
            <div style={{ marginTop: 12 }}>
              <button onClick={onSignup} style={linkStyle}>New here? Sign up</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // --- Trusted device: family faces ---
  const selectedName = family.find((f) => f.id === selectedId)?.name ?? "";
  return (
    <div style={pageStyle}>
      <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, color: ChromeTokens.color.ink }}>Welcome back!</h1>
      <p style={{ margin: 0, fontSize: 14, color: ChromeTokens.color.inkMuted }}>Tap your picture to jump in</p>

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", justifyContent: "center", marginTop: 16 }}>
        {family.map((p) => {
          const selected = selectedId === p.id;
          return (
            <button
              key={p.id}
              onClick={() => handleTap(p)}
              aria-label={p.grown ? `${p.name}, grown-up — needs a password` : `${p.name}, tap to sign in`}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                padding: 14, borderRadius: ChromeTokens.radius.lg, minWidth: 104, cursor: "pointer",
                background: ChromeTokens.color.base,
                boxShadow: selected ? ChromeTokens.shadow.lifted : ChromeTokens.shadow.soft,
                border: selected ? `2px solid ${ChromeTokens.color.sun}` : "2px solid transparent",
                fontFamily: ChromeTokens.font.ui,
              }}
            >
              <div style={{ position: "relative" }}>
                <img
                  src={p.avatar_url ?? getSilhouetteUrls(p.id).portrait_128}
                  alt=""
                  style={{ width: 72, height: 72, borderRadius: ChromeTokens.radius.pill, objectFit: "cover", objectPosition: "center top", background: ChromeTokens.color.baseMuted }}
                  onError={(e) => { e.currentTarget.src = getSilhouetteUrls(p.id).portrait_128; }}
                />
                <span
                  title={p.grown ? "Grown-up — enter password" : "Tap to enter — no password needed"}
                  style={{
                    position: "absolute", bottom: -2, right: -2, width: 22, height: 22, borderRadius: "50%",
                    background: p.grown ? ChromeTokens.color.baseMuted : ChromeTokens.color.grass,
                    border: `2px solid ${ChromeTokens.color.base}`, display: "grid", placeItems: "center",
                  }}
                >
                  <Icon name={p.grown ? "lock" : "check"} size={12} stroke={p.grown ? ChromeTokens.color.inkMuted : "#fff"} />
                </span>
              </div>
              <span style={{ fontSize: 14, fontWeight: 700, color: ChromeTokens.color.ink }}>{p.name}</span>
              <span style={{ fontSize: 11, color: ChromeTokens.color.inkMuted }}>{p.grown ? "grown-up" : "tap to enter"}</span>
            </button>
          );
        })}
      </div>

      {/* Grown-up password (revealed when a grown-up face is selected) */}
      {selectedId && !submitting && (
        <form onSubmit={handleGrownSubmit} style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%", maxWidth: 280, marginTop: 16 }}>
          <input
            type="password"
            placeholder={`${selectedName}'s password`}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            autoComplete="current-password"
            style={inputStyle}
            required
          />
          {error && <p style={{ color: ChromeTokens.color.danger, fontSize: 13, margin: 0 }}>{error}</p>}
          <button type="submit" disabled={!password} style={goBtnStyle}>Go</button>
        </form>
      )}

      {submitting && <p style={{ fontSize: 14, color: ChromeTokens.color.inkMuted, marginTop: 12 }}>Signing in…</p>}
      {error && !selectedId && <p style={{ color: ChromeTokens.color.danger, fontSize: 13, marginTop: 8 }}>{error}</p>}

      <div style={{ display: "flex", gap: 20, marginTop: 24 }}>
        <button onClick={onNewPerson} style={linkStyle}>Someone else?</button>
        <span style={{ color: ChromeTokens.color.baseMuted }}>·</span>
        <button onClick={forget} style={linkStyle}>Forget this device</button>
      </div>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
  minHeight: "100vh", padding: 24, gap: 8,
  background: `linear-gradient(180deg, ${ChromeTokens.color.sky} 0%, ${ChromeTokens.color.base} 100%)`,
};

const cardStyle: React.CSSProperties = {
  width: "100%", maxWidth: 420, padding: 28, borderRadius: ChromeTokens.radius.lg,
  background: `color-mix(in srgb, ${ChromeTokens.color.base} 92%, transparent)`,
  boxShadow: ChromeTokens.shadow.lifted,
};

const lockChipStyle: React.CSSProperties = {
  width: 56, height: 56, borderRadius: ChromeTokens.radius.pill, background: ChromeTokens.color.baseMuted,
  display: "grid", placeItems: "center", margin: "0 auto 14px",
};

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "11px 14px", fontSize: 15, borderRadius: ChromeTokens.radius.md,
  border: `1.5px solid ${ChromeTokens.color.baseMuted}`, background: ChromeTokens.color.base,
  color: ChromeTokens.color.ink, outline: "none", fontFamily: ChromeTokens.font.ui, boxSizing: "border-box",
};

const goBtnStyle: React.CSSProperties = {
  padding: "10px 20px", borderRadius: ChromeTokens.radius.pill, border: "none",
  cursor: "pointer", fontSize: 15, fontWeight: 700,
  background: ChromeTokens.color.sun, color: ChromeTokens.color.ink, fontFamily: ChromeTokens.font.ui,
};

const primaryBtnStyle: React.CSSProperties = {
  width: "100%", padding: "12px 20px", borderRadius: ChromeTokens.radius.pill, border: "none",
  cursor: "pointer", fontSize: 15, fontWeight: 700,
  background: ChromeTokens.color.sun, color: ChromeTokens.color.ink, fontFamily: ChromeTokens.font.ui,
};

const linkStyle: React.CSSProperties = {
  background: "none", border: "none", color: ChromeTokens.color.inkMuted,
  fontSize: 13, cursor: "pointer",
};
